import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Database, RefreshCw, AlertTriangle, Save } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { EmptyState, CardSkeleton } from '../common/States';
import { api } from '../../services/api';
import { getErrorMessage } from '../../lib/utils';

/**
 * The config store, with a screen behind it at last.
 *
 * `platform_baseline_definitions` seeds every tenant provisioning and now carries
 * the literal content the superadmin screens render, but nothing could edit it
 * except a few purpose-built endpoints. A bad payload here breaks the next
 * tenant, so the editor keeps the version it loaded and refuses to save over
 * somebody else's change.
 */
const PlatformDefinitions = () => {
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState('');
  const [jsonError, setJsonError] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/platform-definitions');
      setRows(data.definitions || []);
      setCategories(data.categories || []);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not load platform definitions'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const open = async (row) => {
    try {
      const { data } = await api.get(`/api/platform-definitions/${row.id}`);
      setSelected(data);
      setDraft(JSON.stringify(data.payload_json, null, 2));
      setJsonError('');
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not open that definition'));
    }
  };

  const onDraftChange = (value) => {
    setDraft(value);
    try {
      JSON.parse(value);
      setJsonError('');
    } catch (err) {
      setJsonError(err.message);
    }
  };

  const save = async () => {
    if (!selected || jsonError) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/api/platform-definitions/${selected.id}`, {
        payload_json: JSON.parse(draft),
        expected_version: selected.version,
      });
      setSelected(data);
      setDraft(JSON.stringify(data.payload_json, null, 2));
      toast.success(`Saved — now version ${data.version}`);
      await load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not save that definition'));
    } finally {
      setSaving(false);
    }
  };

  const visible = category ? rows.filter((r) => r.category === category) : rows;

  return (
    <div className="p-1">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-widest text-muted-foreground">
            Master · platform configuration
          </p>
          <h1 className="mt-1 text-2xl font-bold text-foreground">Platform definitions</h1>
          <p className="mt-1 max-w-3xl text-[13px] text-muted-foreground">
            The seed data and literal content the platform reads at runtime. Editing a
            definition takes effect on the next request — no deploy, and no second copy
            in source.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />Refresh
        </Button>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setCategory('')}
          className={`rounded-full border px-3 py-1 text-xs transition-colors ${
            category === '' ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:bg-muted/50'
          }`}
        >
          All ({rows.length})
        </button>
        {categories.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => setCategory(c)}
            className={`rounded-full border px-3 py-1 text-xs transition-colors ${
              category === c ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:bg-muted/50'
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-5">
        <Card className="lg:col-span-2">
          <CardHeader><CardTitle className="text-base">Definitions</CardTitle></CardHeader>
          <CardContent className="max-h-[560px] overflow-y-auto p-0">
            {loading ? <CardSkeleton rows={8} className="border-0" /> : visible.length === 0 ? (
              <EmptyState compact icon={Database} title="Nothing here" description="No definitions in this category." />
            ) : visible.map((row) => (
              <button
                key={row.id}
                type="button"
                onClick={() => open(row)}
                className={`w-full border-b border-border px-3 py-2 text-left transition-colors hover:bg-muted ${
                  selected?.id === row.id ? 'border-l-2 border-l-primary bg-primary/10' : ''
                }`}
              >
                <div className="font-mono text-xs text-foreground">{row.definition_key}</div>
                <div className="mt-1 flex flex-wrap items-center gap-1 text-[11px] text-muted-foreground">
                  <Badge variant="soft">v{row.version}</Badge>
                  <span>{row.payload_type}</span>
                  {row.entry_count !== null && <span>· {row.entry_count} entries</span>}
                  {row.provisioning_critical && (
                    <Badge className="bg-amber-500/15 text-amber-700">seeds new tenants</Badge>
                  )}
                  {!row.is_active && <Badge variant="outline">inactive</Badge>}
                </div>
              </button>
            ))}
          </CardContent>
        </Card>

        <Card className="lg:col-span-3">
          <CardHeader>
            <CardTitle className="text-base">
              {selected ? selected.definition_key : 'Select a definition'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!selected ? (
              <EmptyState icon={Database} title="Nothing open" description="Choose a definition to view or edit its payload." />
            ) : (
              <>
                {selected.provisioning_critical && (
                  <div className="mb-3 flex gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-[13px] text-amber-800">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <div>
                      This definition seeds every new tenant. Emptying it would provision the
                      next workspace without it, so the save is refused rather than accepted.
                    </div>
                  </div>
                )}
                <textarea
                  value={draft}
                  onChange={(e) => onDraftChange(e.target.value)}
                  spellCheck={false}
                  rows={20}
                  className="w-full rounded-md border border-border bg-background p-3 font-mono text-xs text-foreground"
                />
                {jsonError && (
                  <p className="mt-2 text-xs text-danger">Not valid JSON — {jsonError}</p>
                )}
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Button size="sm" onClick={save} disabled={saving || Boolean(jsonError)}>
                    <Save className="mr-1 h-4 w-4" />
                    {saving ? 'Saving…' : `Save as v${(selected.version || 1) + 1}`}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => open(selected)}>Discard changes</Button>
                  <span className="text-[11px] text-muted-foreground">
                    Loaded at v{selected.version}
                    {selected.updated_at ? ` · last saved ${new Date(selected.updated_at).toLocaleString()}` : ''}
                  </span>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PlatformDefinitions;
